<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";

import { useConfetti } from "@/common/composables/use-confetti.composable";
import { randomInRange } from "@/common/utils";

const props = withDefaults(
  defineProps<{
    color?: "primary" | "secondary" | "red";
    hasConfetti?: boolean;
  }>(),
  {
    color: "secondary",
    hasConfetti: false
  }
);

const canvas = ref<HTMLCanvasElement | null>(null);

const { fireConfetti } = useConfetti(canvas, {
  startVelocity: 15,
  decay: 0.95,
  spread: 360,
  ticks: 60,
  zIndex: 0
});

onMounted(() => {
  // Return if we do not want confetti.
  // I know it's sad but some people do not want confetti.
  if (!props.hasConfetti) {
    return;
  }
  const duration = 15 * 1000;
  const animationEnd = Date.now() + duration;

  const interval = setInterval(function () {
    const timeLeft = animationEnd - Date.now();

    if (timeLeft <= 0) {
      return clearInterval(interval);
    }

    // If component is mounted/unmounted instantaneously the canvas could be gone!
    if (!fireConfetti || !canvas.value) {
      return clearInterval(interval);
    }

    const particleCount = 50 * (timeLeft / duration);

    fireConfetti({
      particleCount,
      origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
    });

    fireConfetti({
      particleCount,
      origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
    });
  }, 250);
});

const classes = computed<string[]>(() => ["quiz-jumbotron", `quiz-jumbotron--${props.color}`]);
</script>

<template>
  <div :class="classes">
    <div class="quiz-jumbotron__top">
      <slot />
    </div>
    <div class="quiz-jumbotron__background">
      <canvas v-if="props.hasConfetti" id="confettiCanvas" ref="canvas" />
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.quiz-jumbotron {
  box-shadow: inset 0 -10px 40px -10px rgba(0, 0, 0, 0.1);

  &--primary {
    background-color: rgb(@theme-colors[@primary][10]);
  }

  &--secondary {
    background-color: rgb(@theme-colors[@secondary][10]);
  }

  &--red {
    background-color: rgb(@theme-colors[@red][10]);
  }

  &__top {
    position: relative;
    z-index: 1;
  }

  &__background {
    z-index: 0;
  }
}

#confettiCanvas {
  position: absolute;
  top: 58px;
  width: 100%;
  height: 245px;
}
</style>
